import { DisaggregatedMetric, MetricPanelProps } from "../../services/types";
import { getIndexMetricData, getSortedMetrics, getSvgrMap } from "../../utils/dashboardUtils";
import MetricCard from "../MetricCard";
import SimpleDonutChart from "../charts/SimpleDonutChart";
import IndexBanner from "../IndexBanner";
import SoloIcon from "../SoloIcon";
import MetricBarChart from "../charts/MetricBarChart";
import { useDashboardColor } from "../../context/DashboardColorContext";
import { useCountyIndicators } from "../../context/CountyIndicatorsContext";
import { formatValue } from "../../utils/helpers";

const EconomyMetrics = ({ indicators, metadata, categorizedData, geoDetails, tab, geoTag, selectionActive }: MetricPanelProps) => {  
    const { data: countyIndicators } = useCountyIndicators();
    const color = useDashboardColor(); 
    const sortedMetrics = getSortedMetrics(tab, categorizedData);
    console.log("economy data:", sortedMetrics);

    let metricsForTab = indicators; // Get metrics for the current tab

    // Find the current tab index metric data and id
    const { indexMetricId, indexMetricData } = getIndexMetricData(tab, metadata, indicators);

    // Exclude the index metric from the list of metrics
    if (indexMetricId) {
        metricsForTab = metricsForTab.filter((metric) => metric.id !== indexMetricId);
    }

    if (!metricsForTab || metricsForTab.length === 0) {
        return <div className="flex items-center justify-center text-color-cai-dashboard">No metrics available for {tab}.</div>;
    }

    // Define metrics for order
    const e: DisaggregatedMetric[] = sortedMetrics[0];

    const bachelors = indicators.find(metric => metric.id === 323);
    const ged = indicators.find(metric => metric.id === 325);
    const masters = indicators.find(metric => metric.id === 326);
    const estimatedJobs = indicators.find(metric => metric.id === 328);

    const getCountyDisplayValue = (metricId: number) => {
        const countyMetric = countyIndicators?.find((indicator) => indicator.id === metricId);
        const version = countyMetric?.versions?.[0];

        if (!countyMetric || !version) return null;

        const rawValue =
            version.unit === "ratio"
                ? `${version.percent_calc?.n}/${version.percent_calc?.d}`
                : version.value;

        if (rawValue === undefined || rawValue === null) return null;

        return formatValue(rawValue, version.unit, countyMetric.id);
    };

    const CountyValue = ({ metricId }: { metricId: number }) => {
        const countyValue = getCountyDisplayValue(metricId);

        if (!countyValue) return null;

        return (
            <p className="mt-1 text-xs font-medium text-port-text/80
                opacity-0 max-h-0 overflow-hidden
                translate-y-1
                transition-all duration-200
                group-hover:opacity-100 group-hover:max-h-8 group-hover:translate-y-0">
            King County: <span className="font-semibold text-port-text">{countyValue}</span>
            </p>
        );
    };

    return (
        <>
            <div className="mb-2">
                <IndexBanner
                    key={indexMetricId}
                    metric={indexMetricData}
                    svg={undefined}
                    disaggData={indicators}
                    geoDetails={geoDetails}
                    geoTag={geoTag}
                    selectionActive={selectionActive}
                />
            </div>

            <p className="text-[10px] text-port-text mx-4">
            Activate the <span aria-hidden="true">🛈</span> <strong>button</strong> to expand additional information about a metric.
            </p>
            <p className="text-port-text mx-4 text-[10px]">Data updates published February 27, 2026.</p>

            <div className="mx-auto px-4 pb-8 pt-4">
                <div className="grid grid-cols-2 gap-4">
                    {e.map(metric => (
                        <MetricCard
                            key={metric.id}
                            metric={metric}
                            width="w-full"
                            svg={getSvgrMap(metric.id)}
                            disaggData={indicators}
                            geoDetails={geoDetails}
                            geoTag={geoTag}
                            selectionActive={selectionActive}
                        />
                    ))}

                    <div className="col-span-2 w-full">
                        <div 
                            className="relative overflow-hidden group hover:shadow-md transition-all border rounded-xl bg-white" 
                            style={{ 
                                borderLeftWidth: "4px", borderLeftColor: color,
                            }}
                        >
                            <div className="bg-white p-4">
                                <h3 className="text-sm font-semibold text-port-text mb-2">
                                    Educational Attainment
                                </h3>
                                <div className="flex items-start justify-center gap-4">
                                    {masters && 
                                    <div className="flex flex-col items-start">
                                        <div className="ml-20">
                                            <SoloIcon
                                                key={masters.id}
                                                id={masters.id}
                                                title={masters.name}
                                                svg={undefined}
                                                disaggData={indicators}
                                                geoDetails={geoDetails}
                                                geoTag={geoTag}
                                                selectionActive={selectionActive}
                                            />
                                        </div>
                                        <SimpleDonutChart indicators={indicators} currentTab={tab} chartName="masters" fillColor="#4F92B1" remainColor="#D5D5D5" valueColor="#4D4D4D"/>
                                        <p className="text-port-text text-xs font-semibold mt-2">{masters.name}</p>
                                        <CountyValue metricId={masters.id} />
                                    </div>
                                    }
                                    {bachelors && 
                                    <div className="flex flex-col items-start">
                                        <div className="ml-20">
                                            <SoloIcon
                                                key={bachelors.id}
                                                id={bachelors.id}
                                                title={bachelors.name}
                                                svg={undefined}
                                                disaggData={indicators}
                                                geoDetails={geoDetails}
                                                geoTag={geoTag}
                                                selectionActive={selectionActive}
                                            />
                                        </div>
                                        <SimpleDonutChart indicators={indicators} currentTab={tab} chartName="bachelors" fillColor="#4F92B1" remainColor="#D5D5D5" valueColor="#4D4D4D"/>
                                        <p className="text-port-text text-xs font-semibold mt-2">{bachelors.name}</p>
                                        <div className="mt-0.5 rounded-lg h-4 w-10 px-1.5 text-[10px] text-white text-center" style={{ backgroundColor: color }}>Index</div>
                                        <CountyValue metricId={bachelors.id} />
                                    </div>
                                    }
                                    {ged && 
                                    <div className="flex flex-col items-start">
                                        <div className="ml-20">
                                            <SoloIcon
                                                key={ged.id}
                                                id={ged.id}
                                                title={ged.name}
                                                svg={undefined}
                                                disaggData={indicators}
                                                geoDetails={geoDetails}
                                                geoTag={geoTag}
                                                selectionActive={selectionActive}
                                            />
                                        </div>
                                        <SimpleDonutChart indicators={indicators} currentTab={tab} chartName="ged" fillColor="#4F92B1" remainColor="#D5D5D5" valueColor="#4D4D4D"/>
                                        <p className="text-port-text text-xs font-semibold mt-2">{ged.name}</p>
                                        <CountyValue metricId={ged.id} />
                                    </div>
                                    }
                                </div>
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2258e5]/50 to-[#00ac4e]/50 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                    </div>

                    {estimatedJobs && 
                    <div className="w-full mt-2 col-span-2">
                        <MetricBarChart 
                            metric={estimatedJobs} 
                            disaggData={indicators}
                            geoDetails={geoDetails}
                            geoTag={geoTag}
                            selectionActive={selectionActive}
                            height="h-48" 
                            background="bg-white"
                            barColor={"#4F92B1"}
                            subText="Total Jobs:"
                            showValue={true}
                        />
                    </div>
                    }

                    {/* <div className="col-span-2 w-full mb-4">
                        {categorizedData.Economy.filter(metric => 
                            [getMetricGroupChartId(tab, "estimatedjobs")].includes(metric.id) // Estimated Jobs
                        ).map(metric => (
                            <DisaggregateMetricCardAlt
                                key={metric.id}
                                id={metric.id}
                                metric={metric}
                                title={metric.name}
                                subText="Total Jobs:"
                                svg={getSvgMap(metric.id)}
                                disaggData={indicators}
                                geoDetails={geoDetails}
                                geoTag={geoTag}
                                selectionActive={selectionActive}
                                showValue={true}
                                altFormatting={true}
                            />
                        ))}
                        <SimpleBarChart indicators={indicators} title="Estimated Jobs Total" tab={tab} chartName="estimatedjobs" height="h-72" barColor="#4F92B1" bgColor="#F0F0F0" reverseOrder={false} topFive={false}/>
                    </div> */}
                </div>
            </div>
        </>
    );
}

export default EconomyMetrics;